import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  Self,
} from '@angular/core';
import {
  ControlEvent,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NgControl,
  TouchedChangeEvent,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, filter, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RadioOption } from '../model/radio-option';
import { v4 as uuidv4 } from 'uuid';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';

interface RadioOptionWithId extends RadioOption {
  id: string;
}

interface ControlData {
  isTouched: boolean;
  isRequired: boolean;
  errors: ValidationErrors | null;
  options: RadioOptionWithId[];
}

@Component({
  selector: 'canvas-radio-options',
  templateUrl: './radio-options.component.html',
  styleUrl: './radio-options.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RadioOptionsComponent
  implements ControlValueAccessor, AfterContentInit
{
  private radioControl = new FormControl(null);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  private onChanged = (value: any): void => {};
  private destroyRef = inject(DestroyRef);

  private controlDataSubject = new BehaviorSubject<ControlData>({
    isRequired: false,
    isTouched: false,
    errors: null,
    options: [],
  });

  data$ = this.controlDataSubject.asObservable();

  formGroup: FormGroup;

  faCircleInfo = faCircleInfo;

  @Input() showExclamation = true;
  @Input() label = '';
  @Input() tooltip: string | null = null;

  @Input() set options(values: RadioOption[]) {
    const options = values.map((x) => ({ ...x, id: uuidv4() }));
    this.controlDataSubject.next({ ...this.controlDataSubject.value, options });
  }

  @Input() displayAsRow = false;

  constructor(@Self() private ngControl: NgControl) {
    ngControl.valueAccessor = this;

    this.formGroup = new FormGroup({
      radio: this.radioControl,
    });

    this.radioControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.onChanged(value));

    this.radioControl.events
      .pipe(
        filter((event) => event instanceof TouchedChangeEvent),
        filter((x) => x.touched),
        takeUntilDestroyed()
      )
      .subscribe(() => this.onTouched());
  }

  ngAfterContentInit(): void {
    const control = this.ngControl?.control;

    if (control) {
      this.controlDataSubject.next({
        ...this.controlDataSubject.value,
        isRequired: control.hasValidator(Validators.required),
      });

      control.events
        .pipe(
          map<ControlEvent, ControlData>((event) => {
            let { isTouched } = this.controlDataSubject.value;

            if (event instanceof TouchedChangeEvent) {
              isTouched = event.touched;
            }

            return {
              ...this.controlDataSubject.value,
              isRequired: control.hasValidator(Validators.required),
              isTouched,
              errors: control.errors,
            };
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((data) => this.controlDataSubject.next(data));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(value: any) {
    this.radioControl.setValue(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(onChanged: (value: any) => void) {
    this.onChanged = onChanged;
  }

  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    disabled ? this.radioControl.disable() : this.radioControl.enable();
  }
}
